import { toPairs, omit, splitEvery, flatten, pipe, transpose, uniqWith } from 'rambda';
import { SearchOptions, MultipleQueriesResponse, MultipleQueriesQuery } from '@algolia/client-search';
import {
  Refinements,
  notEmpty,
  Product,
  AlgoliaIndiceName,
  DEFAULT_PRODUCT_LIST_HITS_PER_PAGE,
} from '@packages/common';
import { SearchClient } from 'algoliasearch';

export const OPTIONAL_FILTERS_QUERY_HITS_LIMIT = 10;

export const transformFacetFilters = (facetFilters: Refinements['facetFilters']) =>
  toPairs(facetFilters)
    .map(([featureMapping, feature]) => feature.values.map((value) => `${featureMapping}:${value.featureValue}`))
    .filter(notEmpty);

export const transformNumericFilters = (numericFilters: Refinements['numericFilters']) => {
  return toPairs(numericFilters).reduce((acc, next) => {
    const [featureMapping, filter] = next;
    return [...acc, `${featureMapping}>=${filter.value.min}`, `${featureMapping}<=${filter.value.max}`];
  }, [] as any);
};

export const mapRefinementsToAlgoliaQuery = ({ facetFilters, numericFilters }: Refinements) => ({
  facetFilters: transformFacetFilters(facetFilters),
  numericFilters: transformNumericFilters(numericFilters),
});

function createFacetQueriesParams(refinements: Refinements, queryParameters: SearchOptions) {
  const { facetFilters, numericFilters } = refinements;
  const facetList = [...Object.keys(facetFilters), ...Object.keys(numericFilters)];
  return facetList.map((item) => {
    const refinementsWithoutCurrentFilter = {
      facetFilters: omit([`${item}`], facetFilters),
      numericFilters: omit([`${item}`], numericFilters),
    };
    return {
      ...queryParameters,
      hitsPerPage: 1,
      facets: [`${item}`],
      ...mapRefinementsToAlgoliaQuery(refinementsWithoutCurrentFilter as any),
    };
  });
}

function createHitsQuery(
  indexName: AlgoliaIndiceName,
  query: string,
  queryParameters: SearchOptions,
  refinements: Refinements,
  optionalFilters: string[] = [],
): MultipleQueriesQuery {
  let hitsPerPage = queryParameters.hitsPerPage || DEFAULT_PRODUCT_LIST_HITS_PER_PAGE;
  if (optionalFilters.length > 0) {
    hitsPerPage = hitsPerPage - OPTIONAL_FILTERS_QUERY_HITS_LIMIT;
  }
  return {
    indexName,
    query,
    params: { ...queryParameters, hitsPerPage, ...mapRefinementsToAlgoliaQuery(refinements), clickAnalytics: true },
  };
}

function createOptionalFiltersQuery(
  indexName: AlgoliaIndiceName,
  query: string,
  queryParameters: SearchOptions,
  refinements: Refinements,
  optionalFilters: string[] = [],
): MultipleQueriesQuery {
  // If there is no optional filters this query should return 0 items;
  const hitsPerPage =
    optionalFilters.length > 0 ? OPTIONAL_FILTERS_QUERY_HITS_LIMIT * (Number(refinements.page) + 1) : 0;
  return {
    indexName,
    query,
    params: {
      ...queryParameters,
      ...mapRefinementsToAlgoliaQuery(refinements),
      hitsPerPage,
      optionalFilters,
      clickAnalytics: true,
    },
  };
}

export function createQueries(
  indexName: AlgoliaIndiceName,
  query: string,
  queryParameters: SearchOptions,
  refinements: Refinements,
  optionalFilters: string[] = [],
): MultipleQueriesQuery[] {
  const facetQueries = createFacetQueriesParams(refinements, queryParameters).map((params) => ({
    indexName,
    query,
    params: {
      ...params,
    },
  }));
  const optionalFiltersQuery = createOptionalFiltersQuery(
    indexName,
    query,
    queryParameters,
    refinements,
    optionalFilters,
  );
  const hitsQuery = createHitsQuery(indexName, query, queryParameters, refinements, optionalFilters);

  return [hitsQuery, optionalFiltersQuery, ...facetQueries];
}

const transformMultiSearchResponse = (response: MultipleQueriesResponse<Product>, page = 0) => {
  const [main, optionalFilters, ...faceting] = response.results;
  const facets = faceting.reduce((acc, next) => ({ ...acc, ...next.facets }), {});
  const facetsStats = faceting.reduce((acc, next) => ({ ...acc, ...(next as any).facets_stats }), {});

  const paginatedHits = [
    optionalFilters.hits.length > 0 ? splitEvery(optionalFilters.hitsPerPage / (page + 1), optionalFilters.hits) : [],
    splitEvery(main.hitsPerPage / (page + 1), main.hits),
  ];

  const hits = pipe(
    transpose,
    flatten,
    uniqWith((a: Product, b: Product) => a.objectID === b.objectID),
  )(paginatedHits) as Product[];

  return {
    ...main,
    hits,
    // There is a problem with `SearchResponse` type. In its interface they changed facets_stats field to facetsStats
    // but in actual algolia response it is still `facets_stats`
    // same thing goes for line:75
    // We should be careful when we upgrade algolia client library
    facets_stats: { ...(main as any).facets_stats, ...facetsStats },
    facets: { ...main.facets, ...facets },
  };
};

interface SearchWithMultipleQueriesPayload {
  indexName: AlgoliaIndiceName;
  queryParameters: SearchOptions;
  refinements: Refinements;
  optionalFilters?: string[];
  query: string;
}

export function searchWithMultipleQueries(
  client: SearchClient,
  { indexName, query, queryParameters, refinements, optionalFilters }: SearchWithMultipleQueriesPayload,
  userToken: string,
): Promise<ReturnType<typeof transformMultiSearchResponse>> {
  const queries = createQueries(indexName, query, { ...queryParameters, userToken }, refinements, optionalFilters);

  return client
    .multipleQueries<Product>(queries)
    .then((response) => transformMultiSearchResponse(response, refinements.page));
}
